import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { SmartLink } from '@/components/shared/SmartLink';
import '@/styles/nymphe-v3.css';
import '@/styles/membership.css';

const fadeUp = {
  initial: { opacity: 0, y: 14 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-10% 0px' },
  transition: { duration: 0.7, ease: [0.2, 0.8, 0.2, 1] },
};

const TIER_OVERVIEW = [
  {
    key: 'silk',
    name: 'Silk',
    monthly: { price: 'Free', pricePer: null, priceSub: 'Complimentary. Invitation required.', badge: 'A quiet entry' },
    annual:  { price: 'Free', pricePer: null, priceSub: 'Complimentary. Invitation required.', badge: 'A quiet entry' },
    note: 'Access. A quiet way in.',
    highlights: [
      'Browse the Discovery grid',
      'Discovery tabs: Nearby, Online, New, Tonight',
      'Basic profile: photos, bio, tags',
      'Send a Kiss to connect',
      'Messaging opens when it&rsquo;s mutual',
      'Limited daily Kiss',
    ],
    cta: 'Request invitation',
    ctaTo: '/access',
  },
  {
    key: 'velvet',
    name: 'Velvet',
    monthly: { price: '$9.99',  pricePer: '/ mo', priceSub: 'Billed monthly in AUD.',            badge: 'Most chosen' },
    annual:  { price: '$99', pricePer: '/ yr', priceSub: 'Billed annually in AUD. Save $20.', badge: 'Save $20' },
    note: 'Organisation + flow. More signal, less noise.',
    highlights: [
      'Everything in Silk',
      'Subtle priority placement',
      'Advanced discovery filters',
      'Inbox tabs + 3&ndash;5 pinned chats',
      'Voice notes &amp; media in chat',
      'See who viewed you',
      'Unlimited Kiss',
      'Basic private photo gallery',
    ],
    cta: 'Step into Velvet',
    ctaTo: '/access',
  },
  {
    key: 'noir',
    name: 'Noir',
    monthly: { price: '$19.99',  pricePer: '/ mo', priceSub: '3-day trial, then AUD $19.99 per month.', badge: '3 days free' },
    annual:  { price: '$199', pricePer: '/ yr', priceSub: 'Billed annually in AUD. Save $40.',       badge: 'Save $40' },
    note: 'Control, privacy, and deeper media power.',
    highlights: [
      'Everything in Velvet',
      'Higher priority + reorder &amp; 10&ndash;15 pins',
      'Advanced inbox filters',
      'Video messages &amp; video calls',
      'Read receipts &amp; control who can message you',
      'Incognito Mode &amp; Anonymous Viewing',
      'Timer-based media + screenshot awareness',
      'Multiple private galleries + private video gallery',
    ],
    cta: 'Enter Noir',
    ctaTo: '/access',
  },
];

// Grouped comparison content — sourced verbatim from the NYMPHÉ Membership
// Comparison Chart. Five categories, scannable per group.
const T = true;
const F = false;
const COMPARE_GROUPS = [
  {
    title: 'Access & Discovery',
    rows: [
      { label: 'Browse Discovery grid',                                 silk: T, velvet: T, noir: T },
      { label: 'Discovery tabs: Nearby / Online / New / Tonight',       silk: T, velvet: T, noir: T },
      { label: 'Open messaging',                                        silk: T, velvet: T, noir: T },
      { label: 'Basic profile: photos, bio, tags',                      silk: T, velvet: T, noir: T },
      { label: 'Priority placement in grid',                            silk: F, velvet: 'Subtle priority', noir: 'Higher priority' },
      { label: 'Advanced discovery filters',                            silk: F, velvet: T, noir: T },
    ],
  },
  {
    title: 'Inbox',
    rows: [
      { label: 'Chronological inbox',                                   silk: T, velvet: T, noir: T },
      { label: 'Inbox tabs: All / Unread / Online Now / Recent',        silk: F, velvet: T, noir: T },
      { label: 'Pin conversations',                                     silk: F, velvet: '3–5 pins', noir: '10–15 pins' },
      { label: 'Reorder pinned chats',                                  silk: F, velvet: F, noir: T },
      { label: 'Advanced inbox filters: verified / active / kissed',    silk: F, velvet: F, noir: T },
    ],
  },
  {
    title: 'Messaging & Interaction',
    rows: [
      { label: 'Text messaging',                                        silk: T, velvet: T, noir: T },
      { label: 'Kiss signal',                                           silk: 'Limited daily', velvet: 'Unlimited', noir: 'Unlimited' },
      { label: 'Media sharing in chat',                                 silk: F, velvet: T, noir: T },
      { label: 'Voice notes',                                           silk: F, velvet: T, noir: T },
      { label: 'Read receipts',                                         silk: F, velvet: F, noir: T },
      { label: 'Video messages',                                        silk: F, velvet: F, noir: T, featured: true },
      { label: 'Video calls',                                           silk: F, velvet: F, noir: T },
    ],
  },
  {
    title: 'Media Safety & Private Gallery',
    rows: [
      { label: 'Explicit media blur + tap to view',                     silk: T, velvet: T, noir: T },
      { label: 'Timer-based media',                                     silk: F, velvet: F, noir: T },
      { label: 'Screenshot awareness',                                  silk: F, velvet: F, noir: T },
      { label: 'Private gallery',                                       silk: F, velvet: 'Basic private photo gallery', noir: 'Expanded private gallery', featured: true },
      { label: 'Multiple private galleries',                            silk: F, velvet: F, noir: T },
      { label: 'Private video gallery',                                 silk: F, velvet: F, noir: T },
    ],
  },
  {
    title: 'Insight, Privacy & Control',
    rows: [
      { label: 'See who viewed you',                                    silk: F, velvet: T, noir: T, featured: true },
      { label: 'Incognito Mode',                                        silk: F, velvet: F, noir: T, featured: true },
      { label: 'Anonymous Viewing',                                     silk: F, velvet: F, noir: T },
      { label: 'Hide online status',                                    silk: F, velvet: F, noir: T },
      { label: 'Control who can message you',                           silk: F, velvet: F, noir: T },
    ],
  },
];

const MARK = (v) => {
  if (v === true)  return <span className="mb-mark mb-mark--yes" aria-label="Included">✓</span>;
  if (v === false) return <span className="mb-mark mb-mark--no" aria-label="Not included">—</span>;
  return <span className="mb-mark mb-mark--text">{v}</span>;
};

const PREMIUM_VALUES = [
  {
    title: 'Built for how intimacy actually works.',
    body: 'Every tier is shaped around how members read each other — not quick impressions. Silk opens the door. Velvet adds voice, image, and expression. Noir is where you move quietly.',
  },
  {
    title: 'Privacy scales with the tier.',
    body: 'Noir is the only tier where every visibility lever — Incognito Mode, Anonymous Viewing, Hide Online Status, Read Receipts controls — sits in one place. You choose who reads you, and when.',
  },
  {
    title: 'Discretion is the luxury.',
    body: 'NYMPHÉ doesn\'t reward loudness. The premium tiers are designed so the people who want to stay low-visibility still get the richest access — not the other way around.',
  },
];

const MEMBERSHIP_INCLUDES = [
  { title: 'Curated discovery',            body: 'See fewer, better-aligned profiles \u2014 not endless scrolling.' },
  { title: 'Private member profile',       body: 'Control what you reveal, and when.' },
  { title: 'Preference filters',           body: 'Set identity, intention, location, and connection preferences.' },
  { title: 'Mutual-connection messaging',  body: 'Conversations begin when interest is aligned.' },
  { title: 'Visibility controls',          body: 'Choose how, and if, you\u2019re seen.' },
  { title: 'Member support',               body: 'Quiet, direct help when you need it.' },
];

const MEMBERSHIP_AUDIENCE = [
  {
    label: 'For trans women & femmes',
    body: 'A more controlled way to be seen \u2014 with privacy, boundaries, and discretion at the centre.',
  },
  {
    label: 'For respectful admirers',
    body: 'A private space for adults who understand attraction, consent, and respect \u2014 no entitlement, no pressure, no performance.',
  },
];

const BILLING_TERMS = [
  'Velvet: A$9.99 monthly / A$99 annually.',
  'Noir: A$19.99 monthly / A$199 annually.',
  'Noir monthly includes a 3-day free trial where eligible.',
  'Subscriptions renew automatically at the end of each billing period.',
  'Cancel anytime through the Billing portal.',
  'Access continues until the end of the current billing period.',
  'No partial refunds unless required by law.',
  'AUD prices are billed by our payment processor; GST is included where applicable.',
  'You\u2019ll always see the price before you\u2019re charged.',
];

const FAQ_ITEMS = [
  { q: 'Is membership free?',
    a: 'Early access is complimentary to request. Silk, our base tier, is complimentary once you have access. Velvet and Noir are paid subscriptions.' },
  { q: 'What does membership include?',
    a: 'Curated discovery, a private member profile, preference filters, mutual-connection messaging, visibility controls, and member support. Velvet and Noir unlock deeper expression and privacy layers.' },
  { q: 'Is this monthly or annual?',
    a: 'Both. Velvet and Noir offer monthly and annual billing. Annual pricing includes a reduced rate compared to paying monthly for twelve months.' },
  { q: 'Does membership automatically renew?',
    a: 'Yes. Monthly and annual plans auto-renew at the end of each billing period until you cancel.' },
  { q: 'Can I cancel?',
    a: 'Yes. Cancel anytime through the Billing portal. Your access continues until the end of the current billing period.' },
  { q: 'Can I get a refund?',
    a: 'No partial refunds are issued unless required by law. Noir\u2019s 3-day trial converts to the monthly plan on day 4 \u2014 cancel inside the window and you are not charged.' },
  { q: 'Do I need to be approved?',
    a: 'Access opens in waves so NYMPHÉ can grow with care. Availability may vary as new cohorts are released.' },
  { q: 'What happens if I am not approved?',
    a: 'You\u2019ll remain on the waitlist and may be invited into a future access wave.' },
  { q: 'Is NYMPHÉ 18+ only?',
    a: 'Yes. NYMPHÉ is strictly for adults aged 18 and over.' },
  { q: 'Are profiles verified?',
    a: 'Verified presence is part of every profile. Verification uses mutual confirmation and identity signals to support accountable interaction.' },
  { q: 'Can I hide my location?',
    a: 'Yes. Location sharing is optional. Velvet adds granular preference filters. Noir gives full visibility controls \u2014 Incognito Mode, Anonymous Viewing, Hide Online Status, and Read Receipts controls.' },
  { q: 'Are paid arrangements allowed?',
    a: 'No. NYMPHÉ is for private, mutual connection \u2014 not transactional arrangements. Escort-style activity, solicitation, and commercial exchange are prohibited.' },
  { q: 'How do I report someone?',
    a: 'Tap Report on any profile, message, or photo. Our moderation team reviews every report and responds with appropriate action.' },
  { q: 'Can I delete my account and data?',
    a: 'Yes. You can request deletion from Settings or contact support. Your data is removed in line with our Privacy policy.' },
];

const DIFFS_ONLY_KEY = 'nymphe_membership_diffs_only';

const readDiffsOnly = () => {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) return false;
    return window.sessionStorage.getItem(DIFFS_ONLY_KEY) === 'true';
  } catch {
    return false;
  }
};

const MembershipPage = () => {
  const [annual, setAnnual] = useState(false);
  const [diffsOnly, setDiffsOnly] = useState(readDiffsOnly);

  useEffect(() => {
    try {
      if (typeof window === 'undefined' || !window.sessionStorage) return;
      window.sessionStorage.setItem(DIFFS_ONLY_KEY, diffsOnly ? 'true' : 'false');
    } catch {
      /* sessionStorage unavailable — ignore, runtime state still works */
    }
  }, [diffsOnly]);

  // For the "Show differences only" toggle: a row is "shared" when all three
  // tiers carry the same value. We hide those rows when diffsOnly is true,
  // and we hide categories that end up empty after filtering.
  const isAllEqual = (row) => row.silk === row.velvet && row.velvet === row.noir;
  const visibleGroups = diffsOnly
    ? COMPARE_GROUPS
        .map((g) => ({ ...g, rows: g.rows.filter((r) => !isAllEqual(r)) }))
        .filter((g) => g.rows.length > 0)
    : COMPARE_GROUPS;
  return (
    <div className="mb-page" data-testid="membership-page">
      <div className="noise-overlay" />

      {/* ── 1. Hero ── */}
      <section className="mb-hero" data-testid="mb-hero">
        <motion.div className="mb-hero-inner" {...fadeUp}>
          <span className="mb-kicker">Membership</span>
          <h1 className="mb-hero-title">Membership, held privately.</h1>
          <p className="mb-hero-sub">
            Three layers of access, privacy, and control — from entry to
            elevated. Choose the one that fits how you want to move inside
            NYMPHÉ.
          </p>
          <div className="mb-hero-anchor" aria-hidden="true">
            <span className="mb-hero-anchor-line" />
            <span className="mb-hero-anchor-label">Silk &middot; Velvet &middot; Noir</span>
            <span className="mb-hero-anchor-line" />
          </div>
        </motion.div>
      </section>

      {/* ── 1.5 Cinematic break — full-bleed video, immediately under the hero ── */}
      <section className="mb-cinematic" data-testid="mb-cinematic" aria-label="">
        <div className="mb-cinematic-bg" aria-hidden="true">
          <video
            className="mb-cinematic-video"
            data-testid="mb-cinematic-video"
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            aria-hidden="true"
          >
            <source src="/images/mb-cinematic.webm" type="video/webm" />
            <source src="/images/mb-cinematic.mp4"  type="video/mp4" />
          </video>
          <span className="mb-cinematic-plum" />
          <span className="mb-cinematic-vignette" />
          <span className="mb-cinematic-grain" />
        </div>
        <motion.p
          className="mb-cinematic-line"
          data-testid="mb-cinematic-line"
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
        >
          The right people.
          <br />
          The right energy.
        </motion.p>
      </section>

      {/* ── 2. What you're paying for ── */}
      <section className="mb-offer" data-testid="mb-offer">
        <motion.div className="mb-offer-inner" {...fadeUp}>
          <span className="mb-kicker">What you're paying for</span>
          <h2 className="mb-offer-title">NYMPHÉ Member</h2>
          <p className="mb-offer-lede">
            For adults who want deeper access to curated, private discovery.
          </p>

          <div className="mb-offer-split">
            <div className="mb-offer-col" data-testid="mb-offer-free">
              <p className="mb-offer-col-eyebrow">Early Access <span className="mb-offer-col-tag mb-offer-col-tag--free">Free</span></p>
              <ul className="mb-offer-col-list">
                <li>Complimentary early access &mdash; request your invitation</li>
                <li>Create a complimentary Silk account once your wave opens</li>
                <li>Browse the Discovery grid &mdash; send a limited daily Kiss</li>
                <li>On Silk, messaging opens when a Kiss is mutual</li>
              </ul>
            </div>

            <div className="mb-offer-col mb-offer-col--paid" data-testid="mb-offer-paid">
              <p className="mb-offer-col-eyebrow">Member Access <span className="mb-offer-col-tag mb-offer-col-tag--paid">Paid</span></p>
              <ul className="mb-offer-col-list">
                <li>Private gallery and voice notes from <span className="mb-offer-tier-inline">Velvet</span> &mdash; video messages, video calls and timer-based media on <span className="mb-offer-tier-inline">Noir</span></li>
                <li>Unlimited Kiss and richer interaction beyond Silk</li>
                <li>Premium features in <span className="mb-offer-tier-inline">Velvet</span> and <span className="mb-offer-tier-inline">Noir</span> tiers</li>
                <li>Priority visibility and enhanced presence</li>
                <li>Full privacy and visibility controls on <span className="mb-offer-tier-inline">Noir</span> &mdash; Incognito Mode, Anonymous Viewing, Hide Online Status, Read Receipts and message controls</li>
              </ul>
            </div>
          </div>

          <p className="mb-offer-note" data-testid="mb-offer-note">
            Subscriptions renew monthly or annually. Cancel anytime from your Billing portal &mdash; access continues to the end of your current period. No partial refunds. Noir's 3-day trial converts to the monthly plan on day 4; cancel inside the window and you&rsquo;re not charged.
          </p>
        </motion.div>
      </section>

      {/* ── 2.5 Mid-page editorial pause — admirer energy ── */}
      <section className="mb-mid-hero" data-testid="mb-mid-hero" aria-label="">
        <picture className="mb-mid-hero-picture" aria-hidden="true">
          <img
            src="/assets/membership-mid-hero.png"
            alt=""
            loading="lazy"
            decoding="async"
            className="mb-mid-hero-img"
            data-testid="mb-mid-hero-image"
          />
        </picture>
        <div className="mb-mid-hero-tint" aria-hidden="true" />
        <div className="mb-mid-hero-fade" aria-hidden="true" />
        <div className="mb-mid-hero-inner">
          <p className="mb-mid-hero-eyebrow">Intention</p>
          <h2 className="mb-mid-hero-head">
            Membership is not about more access.<br />It is about better intention.
          </h2>
          <p className="mb-mid-hero-sub">
            Choose the level of presence, privacy, and control that fits the way you move.
          </p>
        </div>
      </section>

      {/* ── 3. Tier overview ── */}
      <section className="mb-tiers" data-testid="mb-tiers">
        <div className="mb-tiers-inner">
          {TIER_OVERVIEW.map((tier, idx) => {
            const p = annual ? tier.annual : tier.monthly;
            return (
              <motion.article
                key={tier.key}
                className={`mb-tier mb-tier--${tier.key}`}
                data-testid={`mb-tier-${tier.key}`}
                data-pricing-state={annual ? 'annual' : 'monthly'}
                initial={{ opacity: 0, y: 22 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-8% 0px' }}
                transition={{ duration: 0.7, delay: 0.08 * idx, ease: [0.2, 0.8, 0.2, 1] }}
              >
                {p.badge && <span className={`mb-tier-badge mb-tier-badge--${tier.key}`}>{p.badge}</span>}
                <header className="mb-tier-head">
                  <h2 className="mb-tier-name">{tier.name}</h2>
                  <div className="mb-tier-price">
                    <span className="mb-tier-price-amt">{p.price}</span>
                    {p.pricePer && <span className="mb-tier-price-per">{p.pricePer}</span>}
                  </div>
                  <span className="mb-tier-price-sub">{p.priceSub}</span>
                </header>
                <p className="mb-tier-note">{tier.note}</p>
                <ul className="mb-tier-highlights">
                  {tier.highlights.map((h, i) => (
                    <li key={i} className="mb-tier-highlight">
                      <span className={`mb-tier-check mb-tier-check--${tier.key}`} aria-hidden="true" />
                      <span dangerouslySetInnerHTML={{ __html: h }} />
                    </li>
                  ))}
                </ul>
                {tier.key === 'noir' && !annual && (
                  <p
                    className="mb-tier-trial-near-cta"
                    data-testid="mb-tier-noir-trial-near-cta"
                  >
                    3-day free trial, then AUD $19.99/month. Cancel anytime before renewal.
                  </p>
                )}
                <SmartLink
                  to={tier.ctaTo}
                  className={`mb-tier-cta mb-tier-cta--${tier.key}`}
                  data-testid={`mb-cta-${tier.key}`}
                >
                  {tier.cta}
                </SmartLink>
              </motion.article>
            );
          })}
        </div>
        <motion.p className="mb-reassurance" data-testid="mb-reassurance" {...fadeUp}>
          Upgrade, switch or step back anytime.
        </motion.p>
        <motion.p className="mb-reassurance mb-reassurance--fine" data-testid="mb-reassurance-fine" {...fadeUp}>
          Secure billing via Stripe &middot; Cancel before renewal &middot; Taxes where applicable
        </motion.p>

        <motion.div className="mb-billing" data-testid="mb-billing" {...fadeUp}>
          <p className="mb-billing-head">Billing clarity</p>
          <ul className="mb-billing-list">
            {BILLING_TERMS.map((t, i) => (
              <li key={i} data-testid={`mb-billing-term-${i}`}>{t}</li>
            ))}
          </ul>
        </motion.div>

        <motion.div className="mb-pricing-trigger-row" {...fadeUp}>
          <button
            type="button"
            className="mb-ui-pricing-trigger mb-ui-pricing-trigger--section"
            onClick={() => setAnnual((v) => !v)}
            aria-pressed={annual}
            data-testid="mb-ui-pricing-trigger"
          >
            {annual ? 'See monthly pricing' : 'See annual pricing'}
          </button>
        </motion.div>
      </section>

      {/* ── What membership includes ── */}
      <section className="mb-includes" data-testid="mb-includes">
        <motion.div className="mb-includes-inner" {...fadeUp}>
          <span className="mb-kicker">What membership includes</span>
          <h2 className="mb-section-title">Every member gets this. Quietly.</h2>
          <div className="mb-includes-grid">
            {MEMBERSHIP_INCLUDES.map((it, i) => (
              <div key={i} className="mb-includes-item" data-testid={`mb-include-${i}`}>
                <h3 className="mb-includes-item-title">{it.title}</h3>
                <p className="mb-includes-item-body">{it.body}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* ── 3. UI showcase ── */}
      <section className="mb-ui" data-testid="mb-ui">
        <motion.div className="mb-ui-head" {...fadeUp}>
          <span className="mb-kicker mb-kicker--plum">Inside the app</span>
          <h2 className="mb-section-title">What each layer looks like in your hand.</h2>
        </motion.div>

        <div className="mb-ui-grid">
          {/* Left — Messages (Interaction / Velvet) */}
          <motion.div className="mb-ui-card mb-ui-card--silk" {...fadeUp}>
            <div className="mb-ui-phone mb-ui-phone--photo">
              <img
                src="/images/nymphe-phone-messages.png"
                alt="Messages — voice notes, media in chat and 'who viewed you' inside NYMPHÉ, unlocked on Velvet."
                className="mb-ui-shot mb-ui-shot--photo"
                data-testid="mb-ui-shot-messages"
                loading="lazy"
                decoding="async"
              />
              <img
                src="/images/nymphe-phone-messages.png"
                alt=""
                aria-hidden="true"
                className="mb-ui-reflection"
                loading="lazy"
                decoding="async"
              />
            </div>
            <div className="mb-ui-caption">
              <span className="mb-ui-caption-tier">Interaction &middot; Velvet</span>
              <span className="mb-ui-caption-text">Voice notes, media in chat and &ldquo;who viewed you&rdquo; &mdash; unlocked on Velvet.</span>
            </div>
          </motion.div>

          {/* Middle — Membership tiers — HERO */}
          <motion.div className="mb-ui-card mb-ui-card--velvet" {...fadeUp}>
            <div className="mb-ui-phone mb-ui-phone--featured mb-ui-phone--photo">
              <img
                src="/images/nymphe-phone-membership.png"
                alt="Membership tiers — Silk, Velvet and Noir layers shown inside NYMPHÉ."
                className="mb-ui-shot mb-ui-shot--photo"
                data-testid="mb-ui-shot-membership"
                loading="lazy"
                decoding="async"
              />
              <img
                src="/images/nymphe-phone-membership.png"
                alt=""
                aria-hidden="true"
                className="mb-ui-reflection"
                loading="lazy"
                decoding="async"
              />
            </div>
            <div className="mb-ui-caption">
              <span className="mb-ui-caption-tier mb-ui-caption-tier--hero">Membership</span>
              <span className="mb-ui-caption-text">Choose the layer that fits.</span>
            </div>
          </motion.div>

          {/* Right — Privacy controls (Discretion / Noir) */}
          <motion.div className="mb-ui-card mb-ui-card--noir" {...fadeUp}>
            <div className="mb-ui-phone mb-ui-phone--photo">
              <img
                src="/images/nymphe-phone-privacy-controls.png"
                alt="Privacy Controls — Incognito Mode, Anonymous Viewing, Hide Online Status and Read Receipts toggles, available on Noir."
                className="mb-ui-shot mb-ui-shot--photo"
                data-testid="mb-ui-shot-privacy"
                loading="lazy"
                decoding="async"
              />
              <img
                src="/images/nymphe-phone-privacy-controls.png"
                alt=""
                aria-hidden="true"
                className="mb-ui-reflection"
                loading="lazy"
                decoding="async"
              />
            </div>
            <div className="mb-ui-caption">
              <span className="mb-ui-caption-tier">Discretion &middot; Noir</span>
              <span className="mb-ui-caption-text">Every visibility lever, in your hand &mdash; on Noir.</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── 4. Comparison chart ── */}
      <section className="mb-compare" data-testid="mb-compare">
        <motion.div className="mb-ui-head" {...fadeUp}>
          <span className="mb-kicker">Compare</span>
          <h2 className="mb-section-title">Side by side.</h2>
          <p className="mb-compare-intro" data-testid="mb-compare-intro">
            Each membership reveals a deeper layer of NYMPHÉ &mdash; from quiet access, to
            smoother connection, to full control over how you&rsquo;re seen, approached, and remembered.
          </p>
        </motion.div>

        {/* Desktop: grouped table */}
        <motion.div className="mb-compare-frame mb-compare-frame--desktop" {...fadeUp}>
          <div className="mb-compare-toolbar">
            <label className="mb-compare-toggle" data-testid="mb-compare-diffs-toggle">
              <input
                type="checkbox"
                className="mb-compare-toggle-input"
                checked={diffsOnly}
                onChange={(e) => setDiffsOnly(e.target.checked)}
                data-testid="mb-compare-diffs-input"
              />
              <span className="mb-compare-toggle-track" aria-hidden="true">
                <span className="mb-compare-toggle-thumb" />
              </span>
              <span className="mb-compare-toggle-label">Show differences only</span>
            </label>
          </div>
          <div className="mb-compare-scroll">
            <table className="mb-compare-table" data-testid="mb-compare-table">
              <thead>
                <tr>
                  <th className="mb-compare-col-feat" scope="col">Feature</th>
                  <th className="mb-compare-col-silk" scope="col">
                    <span className="mb-compare-h">Silk</span>
                  </th>
                  <th className="mb-compare-col-velvet" scope="col">
                    <span className="mb-compare-h">Velvet</span>
                  </th>
                  <th className="mb-compare-col-noir" scope="col">
                    <span className="mb-compare-h">Noir</span>
                  </th>
                </tr>
              </thead>
              {visibleGroups.map((group, gi) => (
                <tbody key={gi} className="mb-compare-group" data-testid={`mb-compare-group-${gi}`}>
                  <tr className="mb-compare-group-header">
                    <th colSpan={4} scope="colgroup" className="mb-compare-group-title">
                      {group.title}
                    </th>
                  </tr>
                  {group.rows.map((row, ri) => (
                    <tr
                      key={ri}
                      data-testid={`mb-compare-row-${gi}-${ri}`}
                      className={row.featured ? 'mb-compare-row--featured' : undefined}
                    >
                      <th scope="row" className="mb-compare-row-label">{row.label}</th>
                      <td className="mb-compare-cell">{MARK(row.silk)}</td>
                      <td className="mb-compare-cell mb-compare-cell--velvet">{MARK(row.velvet)}</td>
                      <td className="mb-compare-cell mb-compare-cell--noir">{MARK(row.noir)}</td>
                    </tr>
                  ))}
                </tbody>
              ))}
            </table>
          </div>
        </motion.div>

        {/* Mobile: accordion of stacked feature cards, one per category */}
        <motion.div className="mb-compare-accordion" data-testid="mb-compare-accordion" {...fadeUp}>
          {COMPARE_GROUPS.map((group, gi) => (
            <details
              key={gi}
              className="mb-compare-acc"
              data-testid={`mb-compare-acc-${gi}`}
              open={gi === 0}
            >
              <summary className="mb-compare-acc-summary">
                <span className="mb-compare-acc-title">{group.title}</span>
                <span className="mb-compare-acc-chev" aria-hidden="true">+</span>
              </summary>
              <ul className="mb-compare-acc-rows">
                {group.rows.map((row, ri) => (
                  <li className="mb-compare-acc-row" key={ri}>
                    <p className="mb-compare-acc-feat">{row.label}</p>
                    <div className="mb-compare-acc-cells">
                      <div className="mb-compare-acc-cell">
                        <span className="mb-compare-acc-tier">Silk</span>
                        {MARK(row.silk)}
                      </div>
                      <div className="mb-compare-acc-cell mb-compare-acc-cell--velvet">
                        <span className="mb-compare-acc-tier">Velvet</span>
                        {MARK(row.velvet)}
                      </div>
                      <div className="mb-compare-acc-cell mb-compare-acc-cell--noir">
                        <span className="mb-compare-acc-tier">Noir</span>
                        {MARK(row.noir)}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </details>
          ))}
        </motion.div>
      </section>

      {/* ── Who membership is for + Safety is standard ── */}
      <section className="mb-audience" data-testid="mb-audience">
        <motion.div className="mb-audience-inner" {...fadeUp}>
          <span className="mb-kicker">Who it's for</span>
          <h2 className="mb-section-title">Designed for who's actually here.</h2>
          <div className="mb-audience-grid">
            {MEMBERSHIP_AUDIENCE.map((a, i) => (
              <div key={i} className="mb-audience-item" data-testid={`mb-audience-${i}`}>
                <p className="mb-audience-label">{a.label}</p>
                <p className="mb-audience-body">{a.body}</p>
              </div>
            ))}
          </div>

          <div className="mb-safety-standard" data-testid="mb-safety-standard">
            <p className="mb-safety-standard-head">Safety is not a premium feature. It&rsquo;s the baseline.</p>
            <p className="mb-safety-standard-body">
              All NYMPHÉ users have access to core safety tools, including block, report, account review, and community standards. Membership enhances the experience; it does not buy special permission to cross boundaries.
            </p>
          </div>
        </motion.div>
      </section>

      {/* ── 5. Premium value ── */}
      <section className="mb-value" data-testid="mb-value">
        <motion.div className="mb-ui-head" {...fadeUp}>
          <span className="mb-kicker mb-kicker--plum">Why tiers exist</span>
          <h2 className="mb-section-title">NYMPHÉ is built for how you want to be seen.</h2>
        </motion.div>
        <div className="mb-value-grid">
          {PREMIUM_VALUES.map((v, i) => (
            <motion.div
              key={i}
              className="mb-value-card"
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.08 * i }}
            >
              <h3 className="mb-value-title">{v.title}</h3>
              <p className="mb-value-body">{v.body}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ── Before you join ── */}
      <section className="mb-commitment" data-testid="mb-commitment">
        <motion.div className="mb-commitment-inner" {...fadeUp}>
          <span className="mb-kicker">Before you join</span>
          <h2 className="mb-commitment-head">
            NYMPHÉ is 18+ only.
            <br />
            Members must follow Community Guidelines.
          </h2>
          <p className="mb-commitment-body">
            Harassment, impersonation, scams, pressure, image-based abuse, and fetishising behaviour are not tolerated.
          </p>
        </motion.div>
      </section>

      {/* ── FAQ ── */}
      <section className="mb-faq" data-testid="mb-faq">
        <motion.div className="mb-faq-inner" {...fadeUp}>
          <span className="mb-kicker">FAQ</span>
          <h2 className="mb-section-title">Before you decide.</h2>
          <div className="mb-faq-list">
            {FAQ_ITEMS.map((item, i) => (
              <details key={i} className="mb-faq-item" data-testid={`mb-faq-item-${i}`}>
                <summary className="mb-faq-q">{item.q}</summary>
                <p className="mb-faq-a">{item.a}</p>
              </details>
            ))}
          </div>
        </motion.div>
      </section>

      {/* ── 6. Final CTA ── */}
      {/* ── Bottom cinematic image — final reinforcement before the CTA ── */}
      <section className="mb-bottom-image" data-testid="mb-bottom-image" aria-label="">
        <div className="mb-bottom-image-bg" aria-hidden="true">
          <picture>
            <source srcSet="/images/mb-hero.webp" type="image/webp" />
            <img
              src="/images/mb-hero.jpg"
              alt=""
              className="mb-bottom-image-img"
              loading="lazy"
              decoding="async"
              draggable="false"
            />
          </picture>
          <span className="mb-bottom-image-plum" />
          <span className="mb-bottom-image-vignette" />
        </div>
      </section>

      {/* ── 7. Final CTA ── */}
      <section className="mb-final" data-testid="mb-final">
        <motion.div className="mb-final-inner" {...fadeUp}>
          <h2 className="mb-final-title">For those who know how to enter softly &mdash; and mean it.</h2>
          <p className="mb-final-sub">
            Silk is complimentary. Velvet opens depth. Noir gives you the full control layer.
            Move between them whenever you want.
          </p>
          <p className="mb-final-sub mb-final-sub--fine" data-testid="mb-final-trust">
            Secure billing &middot; Cancel anytime &middot; No hidden fees
          </p>
          <div className="mb-final-ctas">
            <SmartLink to="/access" className="mb-final-cta mb-final-cta--silk" data-testid="mb-final-silk">
              Request invitation
            </SmartLink>
            <SmartLink to="/access" className="mb-final-cta mb-final-cta--velvet" data-testid="mb-final-velvet">
              Step into Velvet
            </SmartLink>
            <SmartLink to="/access" className="mb-final-cta mb-final-cta--noir" data-testid="mb-final-noir">
              Enter Noir
            </SmartLink>
          </div>
        </motion.div>
      </section>
    </div>
  );
};

export default MembershipPage;
